import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import {
  Wallet,
  Download,
  FileText,
  IndianRupee,
  TrendingUp,
  Calendar,
  CheckCircle,
  Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeeSalaryPage = () => {
  const [loading, setLoading] = useState(true);
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('employeeUser');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchSalaryRecords();
    }
  }, [user]);

  // Set year to most recent salary record year when records load
  useEffect(() => {
    if (salaryRecords.length > 0) {
      const mostRecentYear = Math.max(...salaryRecords.map(r => r.year));
      setSelectedYear(mostRecentYear);
    }
  }, [salaryRecords]);

  const fetchSalaryRecords = async () => {
    try {
      const response = await axios.get(`${API}/salary-records`, {
        params: { employee_id: user.id }
      });
      
      const sorted = response.data.sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
        return months.indexOf(b.month) - months.indexOf(a.month);
      });
      
      setSalaryRecords(sorted);
    } catch (error) {
      console.error('Error fetching salary records:', error);
      toast.error('Failed to fetch salary records');
    } finally {
      setLoading(false);
    }
  };

  const generateSalarySlipPDF = (record) => {
    const doc = new jsPDF();
    
    // ============ HEADER SECTION ============
    // Top border line
    doc.setFillColor(11, 17, 33); // brand-navy
    doc.rect(0, 0, 210, 8, 'F');
    
    // Company Header
    doc.setFillColor(255, 255, 255);
    doc.setTextColor(11, 17, 33);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('ATHARVA FINANCE SOLUTIONS', 105, 22, { align: 'center' });
    
    // Company Address
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Kolhapur, Maharashtra | Contact: 7410001036', 105, 29, { align: 'center' });
    
    // Salary Slip Title Box
    doc.setFillColor(11, 17, 33);
    doc.rect(60, 35, 90, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('SALARY SLIP', 105, 43, { align: 'center' });
    
    // Month/Year Badge
    doc.setFillColor(212, 175, 55); // brand-gold
    doc.rect(75, 50, 60, 8, 'F');
    doc.setTextColor(11, 17, 33);
    doc.setFontSize(10);
    doc.text(`${record.month} ${record.year}`, 105, 55.5, { align: 'center' });
    
    // ============ EMPLOYEE DETAILS SECTION ============
    let y = 68;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.rect(15, y, 180, 28);
    
    // Section Header
    doc.setFillColor(245, 245, 245);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(11, 17, 33);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('EMPLOYEE INFORMATION', 20, y + 5.5);
    
    y += 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    
    // Left Column
    doc.setFont('helvetica', 'bold');
    doc.text('Employee Name:', 20, y);
    doc.setFont('helvetica', 'normal');
    doc.text(record.employee_name, 55, y);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Employee ID:', 20, y + 7);
    doc.setFont('helvetica', 'normal');
    doc.text(user?.id?.substring(0, 8).toUpperCase() || 'N/A', 55, y + 7);
    
    // Right Column
    doc.setFont('helvetica', 'bold');
    doc.text('Department:', 115, y);
    doc.setFont('helvetica', 'normal');
    doc.text('Finance Services', 145, y);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Pay Period:', 115, y + 7);
    doc.setFont('helvetica', 'normal');
    doc.text(`${record.month} ${record.year}`, 145, y + 7);
    
    // ============ EARNINGS & DEDUCTIONS TABLE ============
    y = 105;
    
    // Table Header
    doc.setFillColor(11, 17, 33);
    doc.rect(15, y, 90, 10, 'F');
    doc.rect(105, y, 90, 10, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('EARNINGS', 60, y + 7, { align: 'center' });
    doc.text('DEDUCTIONS', 150, y + 7, { align: 'center' });
    
    // Table Rows
    y += 10;
    doc.setDrawColor(220, 220, 220);
    
    // Row 1
    doc.setFillColor(250, 250, 250);
    doc.rect(15, y, 90, 12, 'F');
    doc.rect(105, y, 90, 12, 'F');
    doc.rect(15, y, 90, 12);
    doc.rect(105, y, 90, 12);
    
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Basic Salary', 20, y + 8);
    doc.setFont('helvetica', 'bold');
    doc.text(`₹ ${record.base_salary.toLocaleString('en-IN')}`, 100, y + 8, { align: 'right' });
    
    doc.setFont('helvetica', 'normal');
    doc.text('Total Deductions', 110, y + 8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 50, 50);
    doc.text(`₹ ${record.deductions.toLocaleString('en-IN')}`, 190, y + 8, { align: 'right' });
    
    // Row 2 - Bonus
    y += 12;
    doc.setFillColor(255, 255, 255);
    doc.rect(15, y, 90, 12, 'F');
    doc.rect(105, y, 90, 12, 'F');
    doc.rect(15, y, 90, 12);
    doc.rect(105, y, 90, 12);
    
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'normal');
    doc.text('Bonus / Incentives', 20, y + 8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(34, 150, 34);
    doc.text(`₹ ${record.bonus.toLocaleString('en-IN')}`, 100, y + 8, { align: 'right' });
    
    // Empty deduction row
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'normal');
    doc.text('-', 150, y + 8, { align: 'center' });
    
    // ============ TOTALS SECTION ============
    y += 20;
    doc.setDrawColor(11, 17, 33);
    doc.setLineWidth(1);
    doc.line(15, y, 195, y);
    
    y += 8;
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Gross Earnings:', 20, y);
    doc.setTextColor(34, 150, 34);
    doc.text(`₹ ${(record.base_salary + record.bonus).toLocaleString('en-IN')}`, 100, y, { align: 'right' });
    
    doc.setTextColor(60, 60, 60);
    doc.text('Total Deductions:', 110, y);
    doc.setTextColor(220, 50, 50);
    doc.text(`₹ ${record.deductions.toLocaleString('en-IN')}`, 190, y, { align: 'right' });
    
    // ============ NET SALARY BOX ============
    y += 15;
    doc.setFillColor(11, 17, 33);
    doc.roundedRect(15, y, 180, 22, 3, 3, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('NET SALARY PAYABLE', 25, y + 10);
    
    doc.setFillColor(212, 175, 55);
    doc.roundedRect(130, y + 3, 60, 16, 2, 2, 'F');
    doc.setTextColor(11, 17, 33);
    doc.setFontSize(14);
    doc.text(`₹ ${record.net_salary.toLocaleString('en-IN')}`, 160, y + 13.5, { align: 'center' });
    
    // ============ AMOUNT IN WORDS ============
    y += 30;
    doc.setFillColor(250, 250, 250);
    doc.rect(15, y, 180, 12, 'F');
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text(`Amount in Words: ${numberToWords(record.net_salary)} Rupees Only`, 20, y + 8);
    
    // ============ PAYMENT STATUS ============
    y += 20;
    const isPaid = record.status === 'paid';
    
    doc.setFillColor(isPaid ? 34 : 234, isPaid ? 197 : 179, isPaid ? 94 : 8);
    doc.roundedRect(15, y, 50, 14, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(isPaid ? 'PAID' : 'PENDING', 40, y + 9.5, { align: 'center' });
    
    if (record.paid_date) {
      doc.setTextColor(80, 80, 80);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Payment Date: ${record.paid_date}`, 75, y + 9);
    }
    
    // ============ FOOTER ============
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(15, 255, 195, 255);
    
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.setFont('helvetica', 'normal');
    doc.text('This is a computer-generated document and does not require a signature.', 105, 262, { align: 'center' });
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}`, 105, 268, { align: 'center' });
    
    // Bottom border
    doc.setFillColor(11, 17, 33);
    doc.rect(0, 289, 210, 8, 'F');
    
    // Save PDF
    doc.save(`Salary_Slip_${record.month}_${record.year}_${record.employee_name.replace(/\s/g, '_')}.pdf`);
    toast.success('Salary slip downloaded successfully!');
  };

  // Helper function to convert number to words
  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
                  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
                  'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (num === 0) return 'Zero';
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    if (num >= 100000) {
      const lakhs = Math.floor(num / 100000);
      const remainder = num % 100000;
      return convertLessThanThousand(lakhs) + ' Lakh' + (remainder ? ' ' + numberToWords(remainder) : '');
    }
    if (num >= 1000) {
      const thousands = Math.floor(num / 1000);
      const remainder = num % 1000;
      return convertLessThanThousand(thousands) + ' Thousand' + (remainder ? ' ' + convertLessThanThousand(remainder) : '');
    }
    return convertLessThanThousand(num);
  };

  const formatCurrency = (amount) => `₹${amount?.toLocaleString('en-IN') || 0}`;

  // Filter by year
  const filteredRecords = salaryRecords.filter(r => r.year === selectedYear);
  const years = [...new Set(salaryRecords.map(r => r.year))].sort((a, b) => b - a);
  if (years.length === 0) years.push(new Date().getFullYear());

  // Stats
  const totalEarned = filteredRecords.reduce((sum, r) => sum + r.net_salary, 0);
  const paidCount = filteredRecords.filter(r => r.status === 'paid').length;
  const pendingCount = filteredRecords.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="employee-salary-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-brand-navy font-heading">Salary Slips</h1>
        <p className="text-slate-500 mt-1">View and download your salary slips</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-brand-navy to-brand-navy-light text-white">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <IndianRupee className="h-5 w-5 text-brand-gold" />
              </div>
              <div>
                <p className="text-xs text-slate-300">Total Earned ({selectedYear})</p>
                <p className="text-xl font-bold">{formatCurrency(totalEarned)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Paid Months</p>
                <p className="text-xl font-bold text-brand-navy">{paidCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pending</p>
                <p className="text-xl font-bold text-brand-navy">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Slips</p>
                <p className="text-xl font-bold text-brand-navy">{salaryRecords.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Salary Records */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-heading text-brand-navy flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Salary History
          </CardTitle>
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]" data-testid="salary-year-select">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {filteredRecords.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No salary records for {selectedYear}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Base Salary</TableHead>
                    <TableHead>Bonus</TableHead>
                    <TableHead>Deductions</TableHead>
                    <TableHead>Net Salary</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          {record.month} {record.year}
                        </div>
                      </TableCell>
                      <TableCell>{formatCurrency(record.base_salary)}</TableCell>
                      <TableCell className="text-emerald-600">+{formatCurrency(record.bonus)}</TableCell>
                      <TableCell className="text-red-600">-{formatCurrency(record.deductions)}</TableCell>
                      <TableCell>
                        <span className="font-semibold text-brand-navy">{formatCurrency(record.net_salary)}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          record.status === 'paid'
                            ? 'bg-emerald-100 text-emerald-700 border-0'
                            : 'bg-amber-100 text-amber-700 border-0'
                        }>
                          {record.status === 'paid' ? 'Paid' : 'Pending'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          onClick={() => generateSalarySlipPDF(record)}
                          data-testid={`download-salary-slip-${record.id}`}
                          variant="outline"
                          size="sm"
                          className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-blue-50 border-blue-100">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-blue-900">About Salary Slips</p>
              <p className="text-sm text-blue-700 mt-1">
                Your salary slips are generated monthly by the HR department. If you notice any discrepancies, 
                please contact HR at 7410001036.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeSalaryPage;
